import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { updateLocationCache } from "@/components/LocationBadge";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ruta_user") || "null"); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem("ruta_token"));
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      updateLocationCache({ country: r.data?.country || null, city: r.data?.city || null });
    }).catch(() => {
      localStorage.removeItem("ruta_token");
      localStorage.removeItem("ruta_user");
      setToken(null); setUser(null);
    }).finally(() => setLoading(false));
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ruta_token", data.token);
    localStorage.setItem("ruta_user", JSON.stringify(data.user));
    try { sessionStorage.removeItem("ruta_smart_routed"); } catch {}
    setToken(data.token); setUser(data.user);
    updateLocationCache({ country: data.user?.country || null, city: data.user?.city || null });
    return data;
  }, []);

  const register = useCallback(async (email, password, name, extras = {}) => {
    const { data } = await api.post("/auth/register", { email, password, name, ...extras });
    localStorage.setItem("ruta_token", data.token);
    localStorage.setItem("ruta_user", JSON.stringify(data.user));
    try { sessionStorage.removeItem("ruta_smart_routed"); } catch {}
    setToken(data.token); setUser(data.user);
    updateLocationCache({ country: data.user?.country || null, city: data.user?.city || null });
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("ruta_token");
    localStorage.removeItem("ruta_user");
    localStorage.removeItem("ruta_ai_session");
    // NOTE: intentionally preserve ruta_last_route so SmartEntry can return
    // the user to their most recent working location on the next login.
    try { sessionStorage.removeItem("ruta_smart_routed"); } catch {}
    setToken(null); setUser(null);
    updateLocationCache(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() { return useContext(AuthCtx); }
